import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Redirect, useHistory } from 'react-router-dom'

// libs
import { TagManager, dateUtils } from '@navent-jobs/config'

import { RedirectHeaderMeta } from '@navent-jobs/ui-kit/components/redirect-prerender-meta'
import { useWindowSize } from '@navent-jobs/ui-kit/hooks/window-size'
import { MainContainer, Row, Col } from '@navent-jobs/ui-kit/components/grid'

// services
import fichaAvisoService from '../../../../services/ficha-aviso-service'
import mailService from '../../../../services/mail-service'

// components
import FichaAviso from '../../../../components/ficha-aviso'
import PostularBar from '../postular-bar'
import ASalarios from '../salarios'
import HeaderFicha from '../ficha-header'
import AnchorBar from './components/AnchorBar'
import Placeholder from './components/AnchorBar/Placeholder'
import Actions from './components/SubHeader/Actions'
import LookAndFeel from './components/LookAndFeel'
import SectionEmpresa from '../../../../components/ficha-aviso/components/Sections/Empresa'
import FichaAvisoEmpresaPlaceholderDesktop from '../../../../components/ficha-aviso/components/Placeholder/FichaAvisoEmpresaPlaceholderDesktop'
import SectionEstadisticas from '../../../../components/ficha-aviso/components/Sections/Estadisticas'
import Legales from '../../../../components/ficha-aviso/components/Legales'

// mixins
import {
  MainPage,
  StickyContainer,
  StickyContainerHeader,
  StickyContainerWrapper,
  NavWrapper,
  ButtonBack,
  StickyContainerFooter,
  AnchorBarBackground,
} from './mixins'

// constants
import features from '../../../../constants/features-per-country'
import { NOMBRE_SITIO, SITE_ID } from '../../../../constants'
import { DataLayers } from '../../../../constants/gtm-events-ficha-aviso-page'

// redux
import { setActiveAviso } from '../../../../redux/actions/active-aviso'

// utils
import { userLoggedIn } from '../../../../utils/cookies'
import { scrollToSection } from '../../utils/scroll-to-section'
import { currentURL, getRedirectlUrl, wrongUrl } from '../../utils/redirect-to-ficha'
import { goBackOrHome } from '../../../../utils/navigation-util'
import { esSeguidor } from '../../../../redux/actions/company-summary'
import { makeURL } from '../../../../utils/search-avisos-v2'
import { getPreferenciaSalarial } from '../../../../redux/actions/applicant/curriculum/preferencia-salarial'

const site = SITE_ID || 'BMAR'
const topHeaderDesktop = 100
const seguirEmpresaEnabled = features[site].seguirEmpresa.enabled

interface RouteFichaProps {
  match: {
    isExact: boolean
    params?: {
      idAviso: string
    }
    path: string
    url: string
  }
  autologin?
  activeAviso?
  applicant?: {
    id: string
    email: string
  }
  preferenciaSalarial?
  preferenciaSalarialStatus?
}

const onActiveAvisoChange = (activeAviso, isUserLoggedIn, dispatch) => {
  if (activeAviso.aviso) {
    TagManager.Push(
      DataLayers.onload({
        aviso: activeAviso.aviso,
        seccion: 'Aviso',
      })
    )

    if (seguirEmpresaEnabled && isUserLoggedIn && activeAviso.aviso.empresa.id) {
      dispatch(esSeguidor(activeAviso.aviso.empresa.id))
    }
  }
}

const searchPreferenciaSalarial = (isUserLoggedIn, dispatch) => {
  if (isUserLoggedIn) {
    dispatch(getPreferenciaSalarial())
  }
}

const updateSalarioPretendidoDefault = (preferenciaSalarialStatus, preferenciaSalarial, setSalarioPretendido) => {
  if (preferenciaSalarialStatus.success && preferenciaSalarial) {
    setSalarioPretendido(preferenciaSalarial.toString())
  }
}

const RouteFicha = ({
  match,
  activeAviso,
  applicant,
  autologin,
  preferenciaSalarial,
  preferenciaSalarialStatus,
}: RouteFichaProps) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isUserLoggedIn = userLoggedIn()

  const [showFicha, setShowFicha] = useState(!autologin)
  const [isNavResized, setNavResize] = useState(false)
  const [showAnchorPostular, setShowAnchorPostular] = useState(false)
  const [visibleModal, setVisibleModal] = useState('')
  const [visibleId, setVisibleid] = useState('section-detalle')
  const [salarioBrutoPretendido, setSalarioPretendido] = useState('')

  const { isMobile, isTabletPortrait, isTabletLandscape, isDesktop } = useWindowSize()
  const isDesktopOrTabletLandscape = isDesktop || isTabletLandscape
  const isMobileOrPortrait = isMobile || isTabletPortrait

  const isFeria = window.location.hash.toLocaleLowerCase().includes('origen_feria')
  const postularme = window.location.hash.toLocaleLowerCase().includes('postularme')

  const idApplicant = applicant?.id
  const emailApplicant = applicant?.email
  const nombreSitio = NOMBRE_SITIO

  const salario = activeAviso?.aviso?.requisitos?.salario
  // Fix salario puede ser object o boolean
  const postulacionRapida =
    !(salario === true || salario?.solicitarCandidato) &&
    activeAviso?.aviso?.preguntas?.length === 0 &&
    activeAviso?.aviso?.tipoAviso !== 'freemium'

  // PreRender: Si el estado del aviso es offile/vencido y tiene suficiente tiempo así, damos la directiva code-status 410
  const avisoVencido = activeAviso?.aviso?.estado === 'offline' || activeAviso?.aviso?.estado === 'vencido'
  const diasVencido = dateUtils.getTiempoPublicado(activeAviso?.aviso?.fechaFinalizacion)

  const subHeaderHeight = (document.getElementById('header-component')?.offsetHeight || 0) + 16

  const { error } = activeAviso?.activeAvisoStates

  useEffect(() => {
    const scrollFunction = e => {
      const postularBarBottom = document.getElementById('section-postular-bar')?.getBoundingClientRect().bottom || 0
      const headerHeight = document.getElementById('header-search')?.offsetHeight || 0
      setShowAnchorPostular(postularBarBottom - subHeaderHeight - headerHeight <= 0)

      const scrollTop = e?.srcElement?.documentElement?.scrollTop
      scrollToSection({ scrollTop, setVisibleid, setNavResize })
    }

    window.addEventListener('scroll', scrollFunction, true)

    return () => {
      setNavResize(false)
      window.removeEventListener('scroll', scrollFunction, true)
    }
  }, [isMobile, isTabletPortrait, subHeaderHeight])

  useEffect(() => {
    setShowFicha((autologin && !!applicant) || !autologin)
  }, [applicant, autologin])

  useEffect(() => {
    onActiveAvisoChange(activeAviso, isUserLoggedIn, dispatch)
  }, [activeAviso?.aviso])

  useEffect(() => {
    if (match.params?.idAviso) {
      dispatch(setActiveAviso({ id: match.params.idAviso }))
    }
  }, [match.params?.idAviso])

  useEffect(() => {
    searchPreferenciaSalarial(isUserLoggedIn, dispatch)
  }, [])

  useEffect(() => {
    updateSalarioPretendidoDefault(preferenciaSalarialStatus, preferenciaSalarial, setSalarioPretendido)
  }, [preferenciaSalarialStatus])

  /**
   * Chequeamos que la URL del browser de la Ficha sea la correcta, en caso contrario
   * redirigimos al usuario a la ficha correcta con la URL correcta
   */

  if (wrongUrl({ activeAviso, match })) {
    const redirectURL = getRedirectlUrl(activeAviso)

    return (
      <>
        <RedirectHeaderMeta url={redirectURL} contentCode="301" />
        <Redirect to={redirectURL} />
      </>
    )
  }

  if (error === 404) {
    return (
      <>
        <RedirectHeaderMeta url="/404" contentCode="404" />
        <Redirect to="/404" />
      </>
    )
  }

  if (!showFicha) {
    return null
  }

  return (
    <MainPage>
      {diasVencido > features[site].diasStatusCode410 && avisoVencido && (
        <RedirectHeaderMeta url={currentURL(activeAviso)} contentCode="410" />
      )}

      {isMobileOrPortrait && (
        <NavWrapper id="mobile-top-action-bar">
          <ButtonBack
            onClick={() => goBackOrHome(history)}
            productoLookAndFeel={activeAviso?.aviso?.productoLookAndFeel}
            buttonoptions={{ size: 'medium', type: 'link' }}
            icon={{ name: 'icon-light-arrow-left' }}
          />

          <Actions
            aviso={activeAviso.aviso}
            guardado={activeAviso.guardado}
            isUserLoggedIn={isUserLoggedIn}
            setVisibleModal={setVisibleModal}
            postulacionRapida={postulacionRapida}
          />
        </NavWrapper>
      )}

      {isMobileOrPortrait && <LookAndFeel productoLookAndFeel={activeAviso.productoLookAndFeel} />}

      {!isDesktopOrTabletLandscape && (
        <>
          <StickyContainerWrapper top="48px">
            <MainContainer noGutter={isMobileOrPortrait}>
              <HeaderFicha
                {...activeAviso}
                applicant={applicant}
                isNavResized={isNavResized}
                visibleId={visibleId}
                setVisibleModal={setVisibleModal}
                visibleModal={visibleModal}
                postulacionRapida={postulacionRapida}
              />
            </MainContainer>
          </StickyContainerWrapper>
        </>
      )}

      <MainContainer>
        {isDesktopOrTabletLandscape && (
          <>
            <LookAndFeel productoLookAndFeel={activeAviso.productoLookAndFeel} />
            <StickyContainerHeader top={topHeaderDesktop}>
              <HeaderFicha
                {...activeAviso}
                applicant={applicant}
                setVisibleModal={setVisibleModal}
                visibleModal={visibleModal}
                showAnchorPostular={showAnchorPostular}
                postulacionRapida={postulacionRapida}
              />
            </StickyContainerHeader>
          </>
        )}

        <Row>
          <Col lg={isMobileOrPortrait ? 12 : 8}>
            {isDesktopOrTabletLandscape && !activeAviso.aviso && <Placeholder />}
            {isDesktopOrTabletLandscape && activeAviso.aviso && (
              <StickyContainer top={`${topHeaderDesktop + subHeaderHeight}px`}>
                <Row>
                  <Col>
                    <AnchorBarBackground />
                    <AnchorBar {...activeAviso} visibleId={visibleId} />
                  </Col>
                </Row>
              </StickyContainer>
            )}

            <FichaAviso
              {...activeAviso}
              nombreSitio={nombreSitio}
              fichaAvisoService={fichaAvisoService}
              mailService={mailService}
              urlMaker={makeURL}
              inPageFichaAviso
              postularme={postularme}
              feria={isFeria}
              desdeEmail={autologin}
              dataLayers={DataLayers}
              isNavResized={isNavResized}
              postulacionRapida={postulacionRapida}
              postularBar={
                isMobileOrPortrait && (
                  <StickyContainerFooter id="sticky-postular-bar" isNavResized={isNavResized}>
                    <PostularBar
                      {...activeAviso}
                      fichaAvisoService={fichaAvisoService}
                      urlMaker={makeURL}
                      postularme={postularme}
                      feria={isFeria}
                      desdeEmail={autologin}
                      dataLayers={DataLayers}
                      emailApplicant={emailApplicant}
                      idApplicant={idApplicant}
                      avisoVencido={avisoVencido}
                      isNavResized={isNavResized}
                      isUserLoggedIn={isUserLoggedIn}
                      setSalarioPretendido={setSalarioPretendido}
                      salarioBrutoPretendido={salarioBrutoPretendido}
                      postulacionRapida={postulacionRapida}
                    />
                  </StickyContainerFooter>
                )
              }
            />
          </Col>

          {isDesktopOrTabletLandscape && (
            <Col lg={4}>
              <PostularBar
                {...activeAviso}
                fichaAvisoService={fichaAvisoService}
                urlMaker={makeURL}
                postularme={postularme}
                feria={isFeria}
                desdeEmail={autologin}
                dataLayers={DataLayers}
                emailApplicant={emailApplicant}
                idApplicant={idApplicant}
                avisoVencido={avisoVencido}
                isNavResized={isNavResized}
                isUserLoggedIn={isUserLoggedIn}
                setSalarioPretendido={setSalarioPretendido}
                salarioBrutoPretendido={salarioBrutoPretendido}
                postulacionRapida={postulacionRapida}
              />

              <Legales />
              <SectionEstadisticas />
              {!activeAviso?.aviso ? <FichaAvisoEmpresaPlaceholderDesktop /> : <SectionEmpresa {...activeAviso} />}
              <ASalarios />
            </Col>
          )}
        </Row>
      </MainContainer>
    </MainPage>
  )
}

const states = ({ activeAvisoStore, applicantStore }) => ({
  applicant: applicantStore && applicantStore.applicant,
  activeAviso: activeAvisoStore,
  preferenciaSalarial: applicantStore.preferenciaSalarial,
  preferenciaSalarialStatus: applicantStore.preferenciaSalarialStatus,
})

export default connect(states)(RouteFicha)
